/**
 * Shared client-side builder for .col-card markup from a Shopify
 * /products/{handle}.js (or /recommendations/products.json) product object.
 * Used anywhere a product card needs to be built without a full Liquid
 * render - Recently Viewed on the PDP and collection pages, and (for now)
 * only those. Kept in one place so all such spots render identically and
 * stay in sync with snippets/product-card.liquid's fallback pricing logic.
 */
(function () {
  'use strict';

  function formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    return '₹' + (cents / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  // Mirrors snippets/round-price-charm.liquid: rounds an amount to the
  // nearest whole rupee ending in 9, used as the fallback "compare at"
  // price so cards without a real compare_at_price still show one.
  function roundPriceCharm(cents) {
    var rupees = Math.round(cents / 100);
    var shifted = rupees - 9;
    var lower9 = Math.floor(shifted / 10) * 10 + 9;
    var upper9 = lower9 + 10;
    var final = (upper9 - rupees) < (rupees - lower9) ? upper9 : lower9;
    return Math.max(final, 9) * 100;
  }

  function escapeHTML(str) {
    return String(str).replace(/[&<>'"]/g, function (tag) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[tag] || tag;
    });
  }

  function withWidth(url) {
    if (!url) return '';
    return url + (url.indexOf('?') === -1 ? '?width=600' : '&width=600');
  }

  function mod(n, m) {
    return ((n % m) + m) % m;
  }

  // Mirrors snippets/generated-review-data.liquid exactly (same inputs -
  // product id + days since created_at - must produce the same rating/count
  // whether a card is server-rendered via snippets/product-card.liquid or
  // built here client-side). The rating step uses modular reduction
  // ((a%m)*(b%m))%m instead of (a*b)%m so it stays exact in JS's float
  // arithmetic even for old products where id * daysFactor would exceed
  // Number.MAX_SAFE_INTEGER; Liquid/Ruby has no such limit but the two
  // are mathematically identical.
  function buildRatingData(p) {
    var createdEpoch = Math.floor(new Date(p.created_at).getTime() / 1000);
    var nowEpoch = Math.floor(Date.now() / 1000);
    var daysSince = Math.max(0, Math.floor((nowEpoch - createdEpoch) / 86400));
    var daysFactor = daysSince + 1;
    var ratingStep = mod(mod(p.id, 4) * mod(daysFactor, 4), 4);
    var rating = (ratingStep + 46) / 10;
    var daysCapped = Math.min(daysSince, 365);
    var reviewGap = mod(p.id, 3) + 2;
    var reviewGrowth = Math.floor(daysCapped / reviewGap);
    var countBase = mod(p.id, 40) + 15;
    var reviewCount = countBase + reviewGrowth;
    return { rating: rating, count: reviewCount };
  }

  function buildRatingHTML(p) {
    var data = buildRatingData(p);
    var ratingLabel = data.rating.toFixed(1);
    var ratingPercent = Math.min(100, Math.max(0, data.rating * 20));
    var reviewWord = data.count !== 1 ? 'reviews' : 'review';
    return '<a href="#looxReviews" class="product-rating" aria-label="' + ratingLabel + ' out of 5 stars, ' + data.count + ' ' + reviewWord + '">' +
      '<span class="product-rating__stars" aria-hidden="true">' +
        '<span class="product-rating__stars-bg">★★★★★</span>' +
        '<span class="product-rating__stars-fg" style="width: ' + ratingPercent + '%;">★★★★★</span>' +
      '</span>' +
      '<span class="product-rating__value">' + ratingLabel + '</span>' +
      '<span class="product-rating__count">(' + data.count + ' ' + reviewWord + ')</span>' +
    '</a>';
  }

  function buildProductCard(p) {
    var price = p.price;
    var hasRealCompare = p.compare_at_price && p.compare_at_price > price;
    var comparePrice = hasRealCompare ? p.compare_at_price : roundPriceCharm(price * 1.5);
    var discount = Math.round(((comparePrice - price) * 100) / comparePrice);
    var image = withWidth(p.featured_image);
    var hoverImage = (p.images && p.images.length > 1) ? withWidth(p.images[1]) : '';
    var title = escapeHTML(p.title);

    var card = document.createElement('div');
    card.className = 'col-card';
    card.innerHTML =
      '<div class="col-card__image-wrap">' +
        '<a href="' + p.url + '" class="col-card__link" tabindex="-1" aria-hidden="true"></a>' +
        '<span class="col-card__badge sale">SALE</span>' +
        '<img src="' + image + '" alt="' + title + '" class="col-card__img col-card__img--primary" loading="lazy" width="400" height="500">' +
        (hoverImage ? '<img src="' + hoverImage + '" alt="' + title + '" class="col-card__img col-card__img--hover" loading="lazy" width="400" height="500">' : '') +
        '<button type="button" class="col-card__wishlist" data-wishlist-handle="' + p.handle + '" aria-label="Add to Wishlist">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>' +
        '</button>' +
      '</div>' +
      '<div class="col-card__info">' +
        '<h3 class="col-card__title"><a href="' + p.url + '">' + title + '</a></h3>' +
        buildRatingHTML(p) +
        '<div class="col-card__price-row">' +
          '<span class="col-card__price col-card__price--sale" data-money="' + price + '">' + formatMoney(price) + '</span>' +
          '<span class="col-card__price col-card__price--compare" data-money="' + comparePrice + '">' + formatMoney(comparePrice) + '</span>' +
          '<span class="col-card__discount">' + discount + '% off</span>' +
        '</div>' +
      '</div>';
    return card;
  }

  window.VDProductCardBuilder = {
    formatMoney: formatMoney,
    roundPriceCharm: roundPriceCharm,
    escapeHTML: escapeHTML,
    withWidth: withWidth,
    buildProductCard: buildProductCard
  };
})();
